/**
 * Canvas visual options — token swaps within the existing brand
 * (Anton / Archivo Black, electric blue + signal pink).
 * Switch via VisualOptionsPicker or localStorage key `canvasVisualOption`.
 */

export const VISUAL_OPTIONS = [
  {
    id: 'studio',
    name: 'Studio',
    tagline: 'Brand default · refined forms',
    swatch: ['#eef1f8', '#1a37f0', '#fc2899'],
    vars: {
      '--canvas-bg': '#eef1f8',
      '--canvas-bg-elevated': '#ffffff',
      '--canvas-surface': 'rgba(255, 255, 255, 0.88)',
      '--canvas-border': 'rgba(26, 31, 55, 0.12)',
      '--canvas-border-strong': 'rgba(26, 55, 240, 0.35)',
      '--canvas-text': '#12162a',
      '--canvas-muted': '#5c6478',
      '--canvas-primary': '#1a37f0',
      '--canvas-primary-soft': 'rgba(26, 55, 240, 0.08)',
      '--canvas-accent': '#fc2899',
      '--canvas-focus-ring': 'rgba(26, 55, 240, 0.28)',
      '--canvas-error': '#c62828',
      '--canvas-radius': '8px',
      '--canvas-radius-sm': '6px',
      '--canvas-field-bg': 'rgba(255, 255, 255, 0.95)',
      '--canvas-field-hover': 'rgba(26, 55, 240, 0.04)',
      '--canvas-font-body': '"IBM Plex Sans", "Helvetica Neue", sans-serif',
      '--canvas-font-display': '"Anton", "Archivo Black", sans-serif',
      '--canvas-font-ui': '"Archivo Black", "IBM Plex Sans", sans-serif',
      '--canvas-field-pad-y': '12px',
      '--canvas-field-pad-x': '14px',
      '--canvas-form-gap': '28px',
      '--canvas-label-gap': '8px',
      '--canvas-form-label-gutter': '4px',
      '--canvas-dialog-body-pt': '20px',
      '--canvas-dialog-body-pb': '20px',
      '--canvas-dialog-body-px': '24px',
      '--canvas-dialog-actions-gap': '10px',
      '--canvas-shell-bg':
        'radial-gradient(900px 520px at 12% -10%, rgba(26,55,240,0.09), transparent 55%), radial-gradient(700px 420px at 92% 8%, rgba(252,40,153,0.06), transparent 50%), linear-gradient(165deg, #f4f6fb 0%, #e8ecf6 100%)',
    },
    mui: {
      light: {
        primary: '#1a37f0',
        secondary: '#dc004e',
        backgroundDefault: '#eef1f8',
        backgroundPaper: '#ffffff',
        borderRadius: 8,
      },
      dark: {
        primary: '#FF1493',
        secondary: '#f48fb1',
        backgroundDefault: '#0e1118',
        backgroundPaper: '#171b24',
        borderRadius: 8,
      },
    },
  },
  {
    id: 'blueprint',
    name: 'Blueprint',
    tagline: 'Cool graphite · sharper fields',
    swatch: ['#e6ebf2', '#0b3d91', '#1a37f0'],
    vars: {
      '--canvas-bg': '#e6ebf2',
      '--canvas-bg-elevated': '#f7f9fc',
      '--canvas-surface': 'rgba(247, 249, 252, 0.92)',
      '--canvas-border': 'rgba(15, 35, 70, 0.16)',
      '--canvas-border-strong': 'rgba(11, 61, 145, 0.45)',
      '--canvas-text': '#0f172a',
      '--canvas-muted': '#4a5870',
      '--canvas-primary': '#0b3d91',
      '--canvas-primary-soft': 'rgba(11, 61, 145, 0.08)',
      '--canvas-accent': '#1a37f0',
      '--canvas-focus-ring': 'rgba(11, 61, 145, 0.32)',
      '--canvas-error': '#b71c1c',
      '--canvas-radius': '4px',
      '--canvas-radius-sm': '3px',
      '--canvas-field-bg': '#f7f9fc',
      '--canvas-field-hover': 'rgba(11, 61, 145, 0.05)',
      '--canvas-font-body': '"IBM Plex Sans", "Helvetica Neue", sans-serif',
      '--canvas-font-display': '"Anton", "Archivo Black", sans-serif',
      '--canvas-font-ui': '"Archivo Black", "IBM Plex Sans", sans-serif',
      '--canvas-field-pad-y': '10px',
      '--canvas-field-pad-x': '12px',
      '--canvas-form-gap': '22px',
      '--canvas-label-gap': '6px',
      '--canvas-form-label-gutter': '2px',
      '--canvas-dialog-body-pt': '16px',
      '--canvas-dialog-body-pb': '16px',
      '--canvas-dialog-body-px': '20px',
      '--canvas-dialog-actions-gap': '8px',
      '--canvas-shell-bg':
        'linear-gradient(180deg, #eef2f7 0%, #dde5f0 48%, #e8edf5 100%), repeating-linear-gradient(0deg, transparent, transparent 23px, rgba(11,61,145,0.035) 24px), repeating-linear-gradient(90deg, transparent, transparent 23px, rgba(11,61,145,0.03) 24px)',
    },
    mui: {
      light: {
        primary: '#0b3d91',
        secondary: '#1a37f0',
        backgroundDefault: '#e6ebf2',
        backgroundPaper: '#f7f9fc',
        borderRadius: 4,
      },
      dark: {
        primary: '#5b8def',
        secondary: '#fc2899',
        backgroundDefault: '#0a0f18',
        backgroundPaper: '#121826',
        borderRadius: 4,
      },
    },
  },
  {
    id: 'signal',
    name: 'Signal',
    tagline: 'Higher contrast · pink punch',
    swatch: ['#f2eef5', '#1a37f0', '#e91e8c'],
    vars: {
      '--canvas-bg': '#f2eef5',
      '--canvas-bg-elevated': '#ffffff',
      '--canvas-surface': 'rgba(255, 255, 255, 0.9)',
      '--canvas-border': 'rgba(40, 20, 55, 0.12)',
      '--canvas-border-strong': 'rgba(233, 30, 140, 0.4)',
      '--canvas-text': '#1a1224',
      '--canvas-muted': '#6a5a72',
      '--canvas-primary': '#1a37f0',
      '--canvas-primary-soft': 'rgba(233, 30, 140, 0.07)',
      '--canvas-accent': '#e91e8c',
      '--canvas-focus-ring': 'rgba(233, 30, 140, 0.3)',
      '--canvas-error': '#c62828',
      '--canvas-radius': '10px',
      '--canvas-radius-sm': '7px',
      '--canvas-field-bg': '#ffffff',
      '--canvas-field-hover': 'rgba(233, 30, 140, 0.04)',
      '--canvas-font-body': '"IBM Plex Sans", "Helvetica Neue", sans-serif',
      '--canvas-font-display': '"Anton", "Archivo Black", sans-serif',
      '--canvas-font-ui': '"Archivo Black", "IBM Plex Sans", sans-serif',
      '--canvas-field-pad-y': '13px',
      '--canvas-field-pad-x': '15px',
      '--canvas-form-gap': '30px',
      '--canvas-label-gap': '10px',
      '--canvas-form-label-gutter': '6px',
      '--canvas-dialog-body-pt': '22px',
      '--canvas-dialog-body-pb': '22px',
      '--canvas-dialog-body-px': '26px',
      '--canvas-dialog-actions-gap': '12px',
      '--canvas-shell-bg':
        'radial-gradient(800px 480px at 85% -5%, rgba(233,30,140,0.1), transparent 55%), radial-gradient(700px 400px at 8% 0%, rgba(26,55,240,0.08), transparent 50%), linear-gradient(160deg, #f7f2f8 0%, #ebe8f2 100%)',
    },
    mui: {
      light: {
        primary: '#1a37f0',
        secondary: '#e91e8c',
        backgroundDefault: '#f2eef5',
        backgroundPaper: '#ffffff',
        borderRadius: 10,
      },
      dark: {
        primary: '#ff2d9b',
        secondary: '#7aa2ff',
        backgroundDefault: '#120e16',
        backgroundPaper: '#1c1522',
        borderRadius: 10,
      },
    },
  },
];

export const DEFAULT_VISUAL_OPTION = 'studio';
export const STORAGE_KEY = 'canvasVisualOption';

export function getVisualOption(id) {
  return VISUAL_OPTIONS.find((o) => o.id === id) || VISUAL_OPTIONS[0];
}

export function isVisualOptionId(id) {
  return VISUAL_OPTIONS.some((o) => o.id === id);
}

export function applyVisualOptionVars(option) {
  if (typeof document === 'undefined' || !option?.vars) return;
  const root = document.documentElement;
  root.setAttribute('data-canvas-skin', option.id);
  Object.entries(option.vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}
